import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Breadcrumbs,
  TextField,
  InputAdornment,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  Link,
  Chip,
  Tooltip,
  ListItemSecondaryAction,
} from '@mui/material'
import {
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon,
  SearchOff as SearchOffIcon,
  FormatQuote as FormatQuoteIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'

import type { AddKeyPhrasesViewProps } from './Types'
import { useCommonStyles, useMoveToThemeStyles } from '../Hooks/UseThemeStyles'

const AddKeyPhrasesView: React.FC<AddKeyPhrasesViewProps> = ({
  currentTheme,
  onExitAddMode,
  onAddKeyPhrase,
  allKeyPhrases,
  searchTerm,
  onSearchChange,
  loading,
}) => {
  const [isAdding, setIsAdding] = useState(false)
  const styles = useMoveToThemeStyles()
  const commonStyles = useCommonStyles()

  const sortedAllKeyPhrases = useMemo(
    () => [...allKeyPhrases].sort((a, b) => a.localeCompare(b)),
    [allKeyPhrases],
  )

  // Get current theme's key phrases
  const currentThemeKeyPhrases = useMemo(() => {
    return currentTheme.attributes?.query?.attributes?.key_phrases || []
  }, [currentTheme])

  // Check if a key phrase is already in the current theme
  const isKeyPhraseInTheme = useCallback(
    (keyPhrase: string) => {
      return currentThemeKeyPhrases.includes(keyPhrase)
    },
    [currentThemeKeyPhrases],
  )

  // Handle clicking on a key phrase
  const handleKeyPhraseClick = async (keyPhrase: string) => {
    // Don't do anything if the key phrase is already in the theme or we're already adding
    if (isKeyPhraseInTheme(keyPhrase) || isAdding) return

    setIsAdding(true)
    try {
      const success = await onAddKeyPhrase([keyPhrase])
      if (success) {
        onExitAddMode()
      }
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <Box sx={{ opacity: loading ? 0 : 1 }}>
      <Box sx={styles.breadcrumbsContainer}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            color="primary"
            underline="hover"
            onClick={onExitAddMode}
            sx={styles.backLink}
            tabIndex={0}
            role="button"
            aria-label="Back to key phrases"
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onExitAddMode()
              }
            }}
          >
            <BackIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Typography variant="body2" fontWeight={500}>
              Back
            </Typography>
          </Link>

          <Typography color="text.secondary" variant="body2">
            Add Key Phrases
          </Typography>
        </Breadcrumbs>
      </Box>

      <TextField
        fullWidth
        placeholder="Search key phrases..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        variant="outlined"
        autoComplete="off"
        size="small"
        sx={styles.searchBox}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={styles.destinationHeader}>
        <Typography variant="button" color="text.secondary" fontSize={12}>
          Select key phrase to add
        </Typography>
        <Chip
          label={sortedAllKeyPhrases.length}
          size="small"
          sx={styles.chip}
        />
      </Box>

      {sortedAllKeyPhrases.length > 0 ? (
        <List sx={styles.list}>
          {sortedAllKeyPhrases.map((phrase, index) => {
            const isAlreadyAdded = isKeyPhraseInTheme(phrase)
            const characterLimit = isAlreadyAdded ? 50 : 80
            const isLong = phrase.length > characterLimit

            return (
              <ListItem
                key={index}
                tabIndex={isAlreadyAdded ? -1 : 0}
                onClick={() => handleKeyPhraseClick(phrase)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleKeyPhraseClick(phrase)
                  }
                }}
                sx={{
                  ...styles.listItem,
                  cursor: isAlreadyAdded ? 'default' : 'pointer',
                  '&:hover': {
                    borderColor: isAlreadyAdded ? 'transparent' : 'info.main',
                  },
                }}
              >
                <ListItemIcon sx={styles.listItemIcon}>
                  <FormatQuoteIcon sx={commonStyles.quoteIcon} />
                </ListItemIcon>
                <Tooltip
                  title={isLong ? phrase : ''}
                  placement="top"
                  followCursor
                >
                  <Typography
                    variant="body2"
                    sx={{
                      width: isAlreadyAdded ? 'calc(100% - 80px)' : '100%',
                      opacity: isAlreadyAdded ? 0.3 : 1,
                      ...(isLong ? commonStyles.longText : {}),
                    }}
                  >
                    {phrase}
                  </Typography>
                </Tooltip>
                {isAlreadyAdded && (
                  <ListItemSecondaryAction>
                    <Tooltip
                      title="Already added to this theme"
                      placement="left"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'default',
                        }}
                      >
                        <CheckCircleIcon
                          fontSize="small"
                          sx={{ color: 'success.light', mr: 0.5 }}
                        />
                        <Typography variant="caption" color="success.main">
                          Added
                        </Typography>
                      </Box>
                    </Tooltip>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            )
          })}
        </List>
      ) : (
        <Box sx={styles.noMatchBox}>
          <SearchOffIcon sx={styles.noMatchIcon} />
          <Typography variant="body2" color="text.secondary">
            No key phrases found
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default React.memo(AddKeyPhrasesView)
